import { useState, useContext, useEffect, useRef } from 'react'
import { signInWithEmailAndPassword, getAuth, onAuthStateChanged } from 'firebase/auth'
import { Link, useNavigate } from 'react-router-dom'
import LoadingContext from '../contexts/loading/LoadingContext'
import LogoImg from '../images/logo.png'
import {
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  Button,
  InputRightElement,
  useToast,
  Box,
  Text,
  Container,
  AbsoluteCenter,
  Divider,
  Icon,
  InputLeftElement,
} from '@chakra-ui/react'
import OAuth from '../components/OAuthGoogle/OAuth'
import { Helmet } from 'react-helmet'
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaCar } from 'react-icons/fa'

export default function SignIn() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const { email, password } = formData
  const { buttonLoading, dispatch } = useContext(LoadingContext)

  const navigate = useNavigate()
  const toast = useToast()
  const auth = getAuth()
  const isMounted = useRef(true)

  useEffect(() => {
    if (isMounted) {
      onAuthStateChanged(auth, (user) => {
        if (user) {
          navigate('/')
        }
      })
    }

    return () => {
      isMounted.current = false
    }
  }, [isMounted, auth, navigate])

  const changeHandler = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.id]: e.target.value
    }))
  }

  const submitHandler = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const userCredentials = await signInWithEmailAndPassword(auth, email, password)
      const user = userCredentials.user

      if (user) {
        toast({
          title: 'Welcome back!',
          description: `Hello ${user.displayName || 'User'}, you've successfully signed in.`,
          status: 'success',
          duration: 4000,
          isClosable: true,
          position: 'top',
        })
        navigate('/')
      }
    } catch (error) {
      setLoading(false)
      console.log(error.code)

      let errorTitle = 'Login Failed'
      let errorDescription = 'Something went wrong. Please try again later.'

      switch (error.code) {
        case 'auth/network-request-failed':
          errorDescription = 'Network error! Please check your internet connection.'
          break
        case 'auth/wrong-password':
          errorDescription = 'Incorrect email or password. Please try again.'
          break
        case 'auth/user-not-found':
          errorDescription = 'No account found with this email address.'
          break
        case 'auth/invalid-email':
          errorDescription = 'Please enter a valid email address.'
          break
        case 'auth/too-many-requests':
          errorDescription = 'Too many failed attempts. Please try again later.'
          break
        default:
          errorDescription = 'Something went wrong. Please try again later.'
      }

      toast({
        title: errorTitle,
        description: errorDescription,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      })
    }
  }

  return (
    <>
      <Helmet>
        <title>Sign In | NCPWheels</title>
        <meta
          name="description"
          content="Sign in to NCPWheels and find the best deals for your car"
        />
      </Helmet>

      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgGradient="linear(to-br, gray.50, blue.50)"
        py={12}
        px={4}
        position="relative"
        overflow="hidden"
      >
        {/* Animated background elements */}
        <Box
          position="absolute"
          top="-20%"
          right="-10%"
          w="300px"
          h="300px"
          bgGradient="radial(circle, rgba(59,130,246,0.1) 0%, rgba(59,130,246,0) 70%)"
          borderRadius="full"
          zIndex={0}
        />
        <Box
          position="absolute"
          bottom="-20%"
          left="-10%"
          w="350px"
          h="350px"
          bgGradient="radial(circle, rgba(6,182,212,0.08) 0%, rgba(6,182,212,0) 70%)"
          borderRadius="full"
          zIndex={0}
        />

        <Container maxW="lg" position="relative" zIndex={1}>
          {/* Main Card */}
          <Box
            bg="white"
            borderRadius="3xl"
            boxShadow="2xl"
            overflow="hidden"
            transition="all 0.3s ease"
            _hover={{ boxShadow: "dark-lg" }}
          >
            {/* Decorative top bar */}
            <Box h="1" bgGradient="linear(to-r, blue.400, cyan.400, blue.500)" />

            <Box p={8}>
              {/* Logo and Title */}
              <Box textAlign="center" mb={8}>
                <Box
                  as="img"
                  src={LogoImg}
                  alt="NCPWheels"
                  mx="auto"
                  h="14"
                  mb={4}
                />
                <Text
                  fontSize="3xl"
                  fontWeight="extrabold"
                  bgGradient="linear(to-r, blue.600, cyan.600)"
                  bgClip="text"
                >
                  Welcome Back
                </Text>
                <Text color="gray.500" mt={2} fontSize="sm">
                  Sign in to access your account
                </Text>
              </Box>

              {/* Sign In Form */}
              <form onSubmit={submitHandler}>
                <FormControl mb={5}>
                  <FormLabel htmlFor="email" fontWeight="semibold" color="gray.700">
                    Email Address
                  </FormLabel>
                  <InputGroup size="lg">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaEnvelope} color="gray.400" boxSize={4} />
                    </InputLeftElement>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={changeHandler}
                      required
                      borderRadius="xl"
                      borderColor="gray.200"
                      pl="10"
                      _hover={{ borderColor: "blue.300" }}
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 1px #60A5FA",
                      }}
                    />
                  </InputGroup>
                </FormControl>

                <FormControl mb={4}>
                  <FormLabel htmlFor="password" fontWeight="semibold" color="gray.700">
                    Password
                  </FormLabel>
                  <InputGroup size="lg">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaLock} color="gray.400" boxSize={4} />
                    </InputLeftElement>
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={changeHandler}
                      required
                      borderRadius="xl"
                      borderColor="gray.200"
                      pl="10"
                      pr="4.5rem"
                      _hover={{ borderColor: "blue.300" }}
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 1px #60A5FA",
                      }}
                    />
                    <InputRightElement width="4.5rem" h="full">
                      <Button
                        h="2rem"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        variant="ghost"
                        borderRadius="full"
                      >
                        <Icon as={showPassword ? FaEyeSlash : FaEye} color="gray.500" boxSize={4} />
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                {/* Links */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={6}>
                  <Link to="/sign-up">
                    <Text fontSize="sm" color="blue.600" fontWeight="medium" _hover={{ color: "blue.700", textDecoration: "underline" }}>
                      Create an account
                    </Text>
                  </Link>
                  <Link to="/forgot-password">
                    <Text fontSize="sm" color="blue.600" fontWeight="medium" _hover={{ color: "blue.700", textDecoration: "underline" }}>
                      Forgot password?
                    </Text>
                  </Link>
                </Box>

                {/* Sign In Button */}
                <Button
                  type="submit"
                  isLoading={loading || buttonLoading}
                  loadingText="Signing in..."
                  colorScheme="blue"
                  size="lg"
                  w="full"
                  borderRadius="xl"
                  bgGradient="linear(to-r, blue.500, blue.600)"
                  _hover={{
                    bgGradient: "linear(to-r, blue.600, blue.700)",
                    transform: "translateY(-2px)",
                    boxShadow: "lg",
                  }}
                  _active={{
                    transform: "translateY(0)",
                  }}
                  transition="all 0.2s"
                  mb={6}
                  leftIcon={<FaCar />}
                >
                  Sign In
                </Button>

                {/* Divider */}
                <Box position="relative" mb={6}>
                  <Divider borderColor="gray.200" />
                  <AbsoluteCenter bg="white" px={4}>
                    <Text fontSize="sm" color="gray.500">
                      OR
                    </Text>
                  </AbsoluteCenter>
                </Box>

                {/* OAuth Section */}
                <OAuth />
              </form>
            </Box>
          </Box>

          {/* Footer note */}
          <Text textAlign="center" mt={6} fontSize="sm" color="gray.500">
            By signing in, you agree to our{' '}
            <Text as="span" color="blue.600" cursor="pointer" _hover={{ textDecoration: "underline" }}>
              Terms of Service
            </Text>{' '}
            and{' '}
            <Text as="span" color="blue.600" cursor="pointer" _hover={{ textDecoration: "underline" }}>
              Privacy Policy
            </Text>
          </Text>
        </Container>
      </Box>
    </>
  )
}